import React, { useEffect, useMemo, useState, useRef, lazy, Component } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ShoppingCart, Phone, Mail, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown, FileText, Download, Truck, Calculator as CalcIcon, X, Plus, Minus } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
import { SameDayBadge } from '../components/SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems, getDefaultQuantity, getSmallLoadSurcharge } from '../utils/loadSplit';
import { CartHelper } from '../components/CartHelper';
import { SimpleAreaCalculator } from '../components/SimpleAreaCalculator';
import { ProductFAQs } from '../components/ProductFAQs';
import { getProjects } from '../data/projects';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS } from '../data/cityMaterialContent';
import { JsonLd, useSEO } from '../utils/seo';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-2885" data-mp="3edoarz0xymht">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-[#e8ede4] dark:bg-emerald-500/[0.07] border border-[#d4ddcf] dark:border-emerald-500/20 rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5fb478] focus:border-transparent transition-all flex items-center justify-between font-medium hover:bg-[#dde5d6] dark:hover:bg-emerald-500/[0.12] ${value ? '' : 'text-stone-500'}`} data-id="element-2886" data-mp="3ec3i6ey6tdml">
        <span className="truncate" data-id="element-2887" data-mp="5ye9gt4vllb3">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2888" data-mp="3o2fytw2vsmng" />
      </button>

      <AnimatePresence data-id="element-2889" data-mp="3s39mqpgbq1gc">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full top-full mt-2 max-h-60 overflow-y-auto overflow-x-hidden border border-[#d4ddcf] bg-white pb-2.5 shadow-xl dark:border-white/10 dark:bg-stone-900" data-id="element-2890" data-mp="2coki4iwbw5u4">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-[#e3f7e8] dark:bg-[#0f3e1e] text-[#1a7a3a] dark:text-[#a8e8b8] font-semibold' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-2891" data-mp="2j3jcphja08pq">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-2892" data-mp="a1cbq6ak4xou" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-2893" data-mp="eyuch9ef86lr">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-2894" data-mp="jq2mo7nkrvzz" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2895" data-mp="oodwiidve6i7">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2896" data-mp="tmp7wxjtcp2j">
          <ChevronLeft className="w-5 h-5" data-id="element-2897" data-mp="12mu9gr2bst8s" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2898" data-mp="2zj1jhrswyc4m">
          <ChevronRight className="w-5 h-5" data-id="element-2899" data-mp="2wydema6ozj9t" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-2900" data-mp="2s5k6rdfxfuti">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-2901" data-mp="2n7sfmln3wcqd" data-mpi={i} />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    addToCart,
    openCart
  } = useCart();
  // Lazy-init the default quantity per product. Soil/Mulch/Org. products
  // (topsoil, bark, compost, play chips, fill dirt) default to a 15-yd
  // light load — that's the typical homeowner spring-project size and
  // the size of one full light-material truckload. The 50/50 Mix is
  // the explicit exception (defaults to 12 — see getDefaultQuantity in
  // utils/loadSplit.ts). Everything else (rock, drain, quarry, decor)
  // defaults to a 12-yd heavy load.
  const [quantity, setQuantity] = useState<string>(() => {
    for (const cat of categories) {
      const found = cat.products.find(p => p.slug === slug);
      if (found) return getDefaultQuantity(found.name);
    }
    return '12';
  });
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  // Click-to-expand explainer for the Small-load rate label that now
  // sits ABOVE the big price number when the order is under the
  // small-load threshold. Replaces the verbose "surcharge applies"
  // subtext with a one-tap pulldown of plain-language pricing context.
  const [showSmallLoadInfo, setShowSmallLoadInfo] = useState(false);
  // Auto-dismiss the "Added to Truck!" confirmation card after 10s and
  // revert to the default Add-to-Truck state. Customer can still tap
  // Continue Shopping / View Cart at any point during the window.
  useEffect(() => {
    if (!addedFlash) return;
    const t = setTimeout(() => setAddedFlash(false), 10000);
    return () => clearTimeout(t);
  }, [addedFlash]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  // True when + has been pressed past 4 loads. Replaces the auto-fire
  // QuoteModal — the customer sees an amber "5+ loads" link below the
  // stepper and explicitly taps it to open the form.
  const [showQuoteWarning, setShowQuoteWarning] = useState(false);
  // Free-text yardage input — always active (no tap-to-edit handshake).
  // The readout *is* a transparent input styled to match the bold display.
  // Max 2 digits, clamped 1–96 on blur/Enter. The +/− pads keep this in
  // sync by writing to qtyDraft alongside quantity via the useEffect below.
  const [qtyDraft, setQtyDraft] = useState('12');
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // Sync the input's displayed text whenever quantity changes externally
  // (e.g. via +/− pads, or estimatorQty handoff). Skip the sync if the
  // user is actively typing — we don't want to clobber their keystrokes.
  useEffect(() => {
    if (document.activeElement !== qtyInputRef.current) {
      setQtyDraft(String(Number(quantity) || 12));
    }
  }, [quantity]);
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'docs'>('overview');
  // Custom yardage handed off from the Instant Estimator via ?qty=N (e.g. 17).
  // When set, the page bypasses the standard dropdown and adds-to-cart using
  // the exact estimator yardage via buildDeliveryLineItems, so the 5yd-per-
  // load minimum and the delivery split match the estimator's quote exactly.
  // Clearing it (via the "Change quantity" link) reverts to the dropdown.
  const [estimatorQty, setEstimatorQty] = useState<number | null>(null);
  // Calculator Drawer State
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  const handleUseQuantity = (yards: number) => {
    const roundedYards = Math.ceil(yards);
    if (roundedYards === 0) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (roundedYards <= fullLoad) {
      // Single-load: pre-select the dropdown value at the actual yards the
      // customer asked for. Tiny 1–2 yd loads are accepted as-is and priced
      // with the micro-load formula by the cart's billing math.
      const snapped = Math.max(1, roundedYards);
      setQuantity(String(snapped));
      setNumLoads('');
      setIsCalcOpen(false);
    } else if (product && category) {
      // Multi-load: add directly to cart using the shared splitter so the
      // last partial load is billed at the 5yd minimum (e.g. 16yd → 12 + 5).
      // This avoids the old behavior of inflating qty to fullLoad × loads.
      const lineItems = buildDeliveryLineItems(roundedYards, product.name);
      lineItems.forEach(li => {
        addToCart({
          category: category.name,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: product.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      // Confirmation persists until the customer chooses Continue
      // Shopping or View Cart — gives them time to decide their next
      // step instead of the old 2s auto-dismiss.
      setAddedFlash(true);
      setIsCalcOpen(false);
    }
  };
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Pre-select quantity from estimator handoff (?qty=N). Runs after product
  // is resolved so we know whether it's light (15yd full load) or heavy (12yd).
  // - Single-load amounts (≤ fullLoad) snap to the dropdown directly.
  // - Multi-load amounts (e.g. 17yd) get stored as estimatorQty and the page
  //   adds-to-cart with the exact yardage on click, preserving the estimator's
  //   5yd-min split (12 + 5) rather than inflating to "2 full loads" (24).
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    if (!qtyParam) return;
    const n = parseInt(qtyParam, 10);
    // Accept any positive yardage — manual 1–2 yd entries flow through and
    // get priced with the micro-load formula (flat $250 delivery).
    if (isNaN(n) || n < 1) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (n <= fullLoad) {
      const snapped = Math.max(1, Math.min(fullLoad, n));
      setQuantity(String(snapped));
      setNumLoads('');
      setEstimatorQty(null);
    } else {
      // Keep the exact estimator yardage. Dropdown stays at its default;
      // the Add to Truck button will use estimatorQty via buildDeliveryLineItems.
      setEstimatorQty(n);
    }
  }, [slug, searchParams, product?.name]);
  useSEO({
    title: product && category ? `${product.name} ${category.name === 'Base & Compaction' ? 'Gravel' : ''} | 206Gravel Delivery` : 'Material Not Found | 206Gravel',
    description: product ? `${product.description} Order by the cubic yard with dependable bulk delivery across Seattle, King County, and Pierce County.` : 'Browse bulk gravel, rock, soil, bark, and aggregate delivery from 206Gravel.',
    image: product?.image,
    path: slug ? `/shop/${slug}` : '/shop'
  });
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-2902" data-mp="3eyi39uyzgwrp">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2903" data-mp="3jtfixkqrq4dk">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-2904" data-mp="2ras3olygdjke">
          Return to Home
        </button>
      </div>;
  }
  const relatedProjects = getProjects().filter(project => project.materials.some(material => material.slug === product.slug)).slice(0, 3);
  const supportedCities = SERVICE_PRODUCT_SLUGS.includes(product.slug) ? SERVICE_CITIES.filter(city => ['seattle', 'tacoma', 'bellevue'].includes(city.slug)) : [];
  const isSpecialty = category.name === 'Specialty Rock';
  const pn = product.name.toLowerCase();
  // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
  const isLight = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
  const isLimitedTo12 = product.name === 'Builders Sand';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '5+') {
      setIsQuoteModalOpen(true);
      return;
    }
    // Estimator handoff takes priority — use the exact custom yardage so the
    // cart line items match the estimator's quote (e.g. 14 → 12 + 5, not 24).
    let finalQuantity: number;
    if (estimatorQty && estimatorQty > 0) {
      finalQuantity = estimatorQty;
    } else if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isLight ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    } else {
      finalQuantity = Number(quantity);
    }
    // Split into per-delivery line items so the cart shows a clean breakdown
    // (Delivery 1 / Delivery 2 / ...) and the 5yd-per-load minimum is
    // enforced on any partial last load.
    const lineItems = buildDeliveryLineItems(finalQuantity, product.name);
    lineItems.forEach(li => {
      addToCart({
        category: category.name,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: product.pricePerYard,
        requestedQuantity: li.requestedQuantity
      });
    });
    // Confirmation persists until the customer chooses Continue
    // Shopping or View Cart.
    setAddedFlash(true);
  };
  // Multi-load custom-quote redirect REMOVED. The amber 5+ warning
  // inside the readout window is now the SOLE entry point to the
  // QuoteModal — no auto-fire when numLoads flips to '5+'. The user
  // explicitly has to tap the warning to open the contact form,
  // matching the Hero estimator's behavior.
  // Reset the quantity dropdown to the per-product default whenever the
  // user navigates to a different product. React Router reuses the same
  // <Product /> component instance when only the :slug param changes, so
  // the useState lazy initializer above only runs on FIRST mount. Without
  // this effect, picking Medium Bark after viewing 1.25" Minus would
  // leave the dropdown stuck at 12 yd instead of snapping to the 15 yd
  // light-load default.
  useEffect(() => {
    if (product?.name) {
      setQuantity(getDefaultQuantity(product.name));
      setNumLoads('');
    }
    // Intentionally only re-runs on slug change — manual dropdown
    // adjustments by the customer within the same product are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  return <motion.main initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="py-12 md:py-20 bg-[#f4f7f2] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-2905" data-mp="1v7ft35b5a3n1">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      image: product.images?.length ? product.images : [product.image],
      category: category.name,
      brand: {
        '@type': 'Brand',
        name: '206Gravel'
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: product.pricePerYard,
        url: `https://206gravel.com/shop/${product.slug}`,
        availability: 'https://schema.org/InStock'
      }
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: 'https://206gravel.com/#materials'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: product.name,
        item: `https://206gravel.com/shop/${product.slug}`
      }]
    }]} data-id="element-2906" data-mp="253is42ap9m5r" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2907" data-mp="2zffgwmba0u6p">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-2908" data-mp="2vdtyymjvxppn">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-2909" data-mp="3983az9avgk1q">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2910" data-mp="2gr0n5r9kr227" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-2911" data-mp="2brxii7xiaxur">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-[#fffcf9] dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-2912" data-mp="2brntono76wqn">
                <ChevronLeft className="w-4 h-4" data-id="element-2913" data-mp="n7s15jpdnaji" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2914" data-mp="n8jn02t69twl">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2915" data-mp="1ezhfp6vjhei2">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2916" data-mp="1eyoeelfcnh7b">
                <ChevronLeft className="w-4 h-4" data-id="element-2917" data-mp="111sshse01v9u" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-[#fffcf9] dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-2918" data-mp="15x86ckc0br69">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2919" data-mp="9ruqonb9tubn">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2920" data-mp="3rwy0glc6muie">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-2921" data-mp="3vzb4n5ba6kd2" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2922" data-mp="3vxqdzqs37lkh">
                <ChevronRight className="w-4 h-4" data-id="element-2923" data-mp="3i6ljf33ktjh0" />
              </span>}
          </div>
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(28rem,0.9fr)] md:items-start" data-id="element-2924" data-mp="3mzxxijamq7vi">
          {/* Independent media surface — visually separate from the cart workspace. */}
          <div className="relative h-72 min-w-0 max-w-full overflow-hidden rounded-xl border border-[#e6ece3] bg-stone-200 shadow-sm sm:h-96 md:sticky md:top-6 md:h-[42rem] dark:border-[#262626] dark:bg-[#242424]" data-id="element-2925" data-mp="3d6h0l6f74cnm">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-2926" data-mp="2kmbxe0u18jt6" />
          </div>

          {/* Independent details and ordering surface. */}
          <div className="min-w-0 max-w-full rounded-xl border border-[#e6ece3] bg-[#fffcf9] p-6 shadow-sm sm:p-8 md:p-10 dark:border-[#363534] dark:bg-[#222222]" data-id="element-2927" data-mp="2fpd3njea6evi">
            <div className="mb-2" data-id="element-2928" data-mp="2pkdgumvdel56">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-2929" data-mp="2pnj5k1037l0d">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2930" data-mp="2yl33p12nl16r">
              {product.name}
            </h1>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 dark:border-[#444] mb-6" data-id="element-2931" data-mp="38bo2opm8o7q7">
              <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'overview' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2932" data-mp="3mb5ulduu013s">
                Overview
                {activeTab === 'overview' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2933" data-mp="2jzcaybtjr5n3" />}
              </button>
              <button onClick={() => setActiveTab('specs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'specs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2934" data-mp="2nxrzik5a0m2m">
                Specs
                {activeTab === 'specs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2935" data-mp="37nvmr1bt20jx" />}
              </button>
              <button onClick={() => setActiveTab('docs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'docs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2936" data-mp="3bofic0gqcw3y">
                Documents
                {activeTab === 'docs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2937" data-mp="19sor5f65mphn" />}
              </button>
            </div>

            <div className="mb-6" data-id="element-2938" data-mp="101u4waswrpw7">
              <AnimatePresence mode="wait" data-id="element-2939" data-mp="102wv0giepob2">
                {activeTab === 'overview' && <motion.div key="overview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2940" data-mp="w11u3d4e1cxt">
                    <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-2941" data-mp="1jojget4vvite">
                      {product.description}
                    </p>

                    {product.bestFor && product.bestFor.length > 0 && <div className="mb-6" data-id="element-2942" data-mp="88l147j440jq">
                        <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-2943" data-mp="3bl2uisi8236">
                          Best For
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-2944" data-mp="1vzsieluf1ryo">
                          {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-2945" data-mp="1i8dzsg4f7ss5" data-mpi={idx}>
                              {tag}
                            </span>)}
                        </div>
                      </div>}

                    {product.proTip && <div className="border border-[#d4ddcf] dark:border-emerald-500/20 rounded-sm p-4 flex items-start gap-3" data-id="element-2946" data-mp="20yvhlpnpt3yq">
                        <div className="bg-[#04A05C]/15 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-2947" data-mp="25w3yzo11h3yx">
                          <Check className="w-4 h-4 text-[#04A05C] dark:text-[#22c95c]" data-id="element-2948" data-mp="piduig60cdor" />
                        </div>
                        <div data-id="element-2949" data-mp="ujl2ogc2bevy">
                          <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2950" data-mp="m19ctg4e31jp">
                            Pro Tip
                          </h4>
                          <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-2951" data-mp="hzed2g23ww81">
                            {product.proTip}
                          </p>
                        </div>
                      </div>}
                  </motion.div>}

                {activeTab === 'specs' && <motion.div key="specs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2952" data-mp="3bb899zsvqkt">
                    {product.specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2953" data-mp="3gp4pnzns39bz">
                        {Object.entries(product.specs).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      size: 'Size / Gradation',
                      weightPerYard: 'Density (Weight/Yd)',
                      compaction: 'Compaction Rating',
                      coverage: 'Est. Coverage'
                    };
                    return <div key={key} className="bg-stone-50 dark:bg-[#222222] p-4 rounded-xl border border-stone-200 dark:border-[#333333]" data-id="element-2954" data-mp="1pe1h4oscdi0y">
                              <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-2955" data-mp="wp45h4imyjh6">
                                {labels[key] || key}
                              </p>
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-2956" data-mp="15ohddsr42zzs">
                                {value}
                              </p>
                            </div>;
                  })}
                      </div> : <div className="text-center py-8 text-stone-500" data-id="element-2957" data-mp="2h6ifkpiqnv9y">
                        No detailed specifications available for this product.
                      </div>}
                  </motion.div>}

                {activeTab === 'docs' && <motion.div key="docs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-2958" data-mp="2r7uj1y8lnac0">
                    {/* Auto-generated Spec Sheet — real, downloadable */}
                    <button onClick={() => generateSpecSheet({
                  product,
                  categoryName: category.name
                })} className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all group text-left" data-id="element-2959" data-mp="28h3ed9zk483y">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2960" data-mp="3tvh931aztb7a">
                        <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-2961" data-mp="3tt3dmrcehwz7">
                          <FileText className="w-5 h-5 text-brand-orange" data-id="element-2962" data-mp="kzx3nokobtzh" />
                        </div>
                        <div className="min-w-0" data-id="element-2963" data-mp="790ek7pyhux6">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-2964" data-mp="78i68cq0rq8w">
                            Product Spec Sheet
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-2965" data-mp="3tex8qp53prs2">
                            One-page summary · specs, coverage, pricing
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange shrink-0 ml-3" data-id="element-2966" data-mp="3khmwb0gjrwlk">
                        <Download className="w-4 h-4" data-id="element-2967" data-mp="1io6wpe2hf7eg" />
                        <span className="hidden sm:inline" data-id="element-2968" data-mp="1ioowuls9m5rn">Download</span>
                      </span>
                    </button>

                    {/* Need more? Email us — honest CTA for additional docs */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-2969" data-mp="1xoudflti39tc">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-2970" data-mp="26kai6xvsn9dp">
                        <Mail className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-2971" data-mp="21r8ymckn6lns" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2972" data-mp="2gjj5j7w5lpr3">
                        <p className="text-sm font-bold text-stone-900 dark:text-white mb-0.5" data-id="element-2973" data-mp="2bhae4fyxivr3">
                          Need certified docs?
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed" data-id="element-2974" data-mp="2lgsw4qva47gi">
                          Gradation curves, MSDS, and compaction test data are
                          available on request.{' '}
                          <a href={`mailto:info@206gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-2975" data-mp="r3tkt9xxlzf1">
                            info@206gravel.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div data-id="element-2976" data-mp="3dg4udhntsl8w">
              {isSpecialty ? <div className="space-y-4" data-id="element-2977" data-mp="3dgd7nanv1skm">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-2978" data-mp="38gigr74352zk">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-2979" data-mp="33igyupieb9po">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2980" data-mp="wr7tgb6xvsit">
                    <Phone className="w-5 h-5 mr-2" data-id="element-2981" data-mp="197h667kwufva" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-4" data-id="element-2982" data-mp="1izdfyfwczufc">
                  {(() => {
                const fullLoad = isLight ? 15 : 12;
                const smallLoadThreshold = 10;
                const requestedYards = estimatorQty ? estimatorQty : quantity === 'multi' && numLoads && numLoads !== '5+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                const loads: number[] = [];
                if (requestedYards > 0) {
                  if (requestedYards <= fullLoad) {
                    loads.push(requestedYards);
                  } else {
                    const count = Math.ceil(requestedYards / fullLoad);
                    const last = requestedYards - (count - 1) * fullLoad;
                    for (let i = 0; i < count - 1; i++) loads.push(fullLoad);
                    loads.push(last);
                  }
                }
                const billableYards = loads.reduce((a, b) => a + b, 0);
                const bulkPct = billableYards >= fullLoad * 4 ? 0.1 : billableYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                // Build per-delivery line totals so the multi-load
                // breakdown can mirror the Services hub: each
                // delivery shows its yardage and its post-discount
                // dollar amount on its own row. preDiscountTotal is
                // the strikethrough "Was $X" anchor for the bulk
                // discount headline.
                const deliveryLoads: {
                  yards: number;
                  total: number;
                }[] = [];
                let hasSurchargedLoad = false;
                for (const ld of loads) {
                  let sur = 1;
                  if (ld >= 3 && ld < smallLoadThreshold) {
                    sur = 1 + (smallLoadThreshold - ld) * 0.1;
                  }
                  if (sur > 1) hasSurchargedLoad = true;
                  const loadTotal = Math.ceil(product.pricePerYard * sur) * ld;
                  deliveryLoads.push({
                    yards: ld,
                    total: loadTotal
                  });
                }
                const preDiscountTotal = deliveryLoads.reduce((s, l) => s + l.total, 0);
                let totalCost = preDiscountTotal;
                if (bulkPct > 0) {
                  totalCost = Math.round(totalCost * (1 - bulkPct));
                  // Apply the same bulk discount to each per-delivery
                  // total so the rows sum to the discounted grand
                  // total (with at most a rounding-cent of drift).
                  deliveryLoads.forEach((l, i) => {
                    deliveryLoads[i] = {
                      ...l,
                      total: Math.round(l.total * (1 - bulkPct))
                    };
                  });
                }
                const effectiveAvg = billableYards > 0 ? Math.round(totalCost / billableYards) : product.pricePerYard;
                const showsEffective = billableYards > 0 && (hasSurchargedLoad || bulkPct > 0) && effectiveAvg !== product.pricePerYard;
                // Past 4 full loads the order rolls into custom-quote
                // territory. The bulk-discount formula no longer
                // reflects what dispatch will actually charge (hand
                // loads, mixed truck sizes, etc.), so we suppress
                // the savings copy past that threshold rather than
                // surface a stale number.
                const isCustomQuoteZone = deliveryLoads.length >= 5;
                return <div className="border-b border-stone-100 dark:border-[#444444] pb-4" data-id="element-2983" data-mp="17dn779nf3y9p">
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3" data-id="element-2984" data-mp="139p8h0208ph6">
                          {/* Left: price column. The label above the
                           big number is state-aware:
                           - Bulk discount tier reached: amber pill
                           "5% Bulk Discount" (uppercase, prominent).
                           - Under the small-load threshold: clickable
                           "Small-load rate ⓘ" — tapping toggles a
                           full-width pulldown explainer below the
                           two-column header.
                           - Otherwise: the default "Price" label. */}
                          <div className="min-w-0 flex-1" data-id="element-2985" data-mp="ajdnm0h4ieba">
                            {/* State label — all three variants render
                             at the same H-6 height so the big "$XX
                             /yard" number stays on the same baseline
                             as the right-column total regardless of
                             which label is showing.
                             Priority order:
                             1. Bulk discount (green pill) — customer
                             paying LESS.
                             2. Small-load surcharge (amber pill) —
                             customer paying MORE. Triggers off
                             hasSurchargedLoad alone so the pill
                             appears even on small-load orders where
                             the rounded effective rate happens to
                             match the base rate.
                             3. Default "Price" label otherwise. */}
                            <div className="h-6 mb-1 flex items-center" data-id="element-2986" data-mp="g5pts1d6ojfq">
                              {!isCustomQuoteZone && bulkPct > 0 ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#04A05C] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-2987" data-mp="c6h1l0xsktma">
                                  {Math.round(bulkPct * 100)}% Bulk Discount
                                </span> : !isCustomQuoteZone && hasSurchargedLoad ? <button type="button" onClick={() => setShowSmallLoadInfo(value => !value)} aria-expanded={showSmallLoadInfo} aria-controls="small-load-rate-explainer" className="inline-flex min-h-7 items-center gap-1 rounded-md bg-[#CB6E0A] px-2 py-1 text-[10px] font-bold uppercase leading-none tracking-wide text-white transition-colors hover:bg-[#9a5608] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CB6E0A] focus-visible:ring-offset-2 dark:bg-[#F6A93B] dark:text-stone-900 dark:hover:bg-[#ffc86a] dark:focus-visible:ring-[#F6A93B] dark:focus-visible:ring-offset-[#2a2a2a]" data-id="element-2988" data-mp="14s7223wnmjia">
                                  Small-load rate <Info className="h-3 w-3" aria-hidden="true" data-id="element-2989" data-mp="c15h1m6a5tqu" />
                                </button> : <p className="text-sm text-brand-muted dark:text-[#22c95c] font-medium" data-id="element-2990" data-mp="gzywvl91hwu6">
                                  Price
                                </p>}
                            </div>
                            {isCustomQuoteZone ? <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-2991" data-mp="nj4t3fxzvcbo">
                                Custom quote
                              </p> : <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-2992" data-mp="iih0ipu4anfc">
                                $
                                {showsEffective ? effectiveAvg : product.pricePerYard}{' '}
                                {/* Secondary "/ yard" label uses the
                                 same neutral muted-grey as the right
                                 column's "{N} yds /" and "+ delivery"
                                 so the metadata reads uniformly
                                 regardless of small-load / bulk
                                 discount state. "avg" dropped per
                                 design — the headline rate IS the
                                 customer's per-yard cost. */}
                                <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-2993" data-mp="3d15f9gvrpm6y">
                                  / yard
                                </span>
                              </p>}
                            {/* Subtext under the big number. For a small
                             load: single line with strikethrough base
                             rate + plain "base rate at N yd+" copy
                             (the clickable label above carries the
                             surcharge messaging now). For a bulk
                             discount: single line with strikethrough
                             base rate + "Save $X on N yards". */}
                            {showsEffective && hasSurchargedLoad && !bulkPct && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5 whitespace-nowrap" data-id="element-2994" data-mp="1njhz4apot8oy">
                                  ${product.pricePerYard}/yd at{' '}
                                  {smallLoadThreshold} yd+
                                </p>}
                            {showsEffective && bulkPct > 0 && !isCustomQuoteZone && (
                      /* Savings copy under the price reads
                      accent green (matches the green pill
                      above) — the customer is paying LESS
                      here, so the color reinforces that
                      even when the eye skips the label. */
                      <p className="text-sm mt-1" data-id="element-2995" data-mp="3m08xkqculbhm">
                                  <span className="line-through text-stone-500 dark:text-stone-400" data-id="element-2996" data-mp="3pypvqkeajbmw">
                                    ${product.pricePerYard}/yd
                                  </span>
                                  <span className="text-[#04A05C] ml-2 font-semibold" data-id="element-2997" data-mp="3tuigo2uevhn">
                                    $
                                    {product.pricePerYard * billableYards - totalCost}{' '}
                                    savings
                                  </span>
                                </p>)}
                            {!showsEffective && billableYards === 0 && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5" data-id="element-2998" data-mp="ro4mrynx0uun">
                                Base price at {smallLoadThreshold}+ yards per
                                delivery. Smaller loads include a surcharge.
                              </p>}
                          </div>
                          {/* Right: materials subtotal — mirrors the
                           "$87 / yard" pattern on the left by putting the
                           small qualifier ("12 yds /") IN FRONT of the
                           big number ("$1,044"). The leading-blank top
                           row keeps the big numbers baseline-aligned
                           with the Price column even though the right
                           column has no top label. */}
                          {billableYards > 0 && <div className="min-w-0 text-right" data-id="element-2999" data-mp="3md2ut2bntr8n">
                              <div aria-hidden="true" className="mb-1 h-6" data-id="element-3000" data-mp="3hcnf443wo7ni" />
                              {isCustomQuoteZone ? <>
                                  <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-3001" data-mp="3iuvjvnp8y0ki">
                                    <span className="text-sm font-normal text-brand-muted dark:text-[#22c95c]" data-id="element-3002" data-mp="3nrcff2yrzh7m">
                                      {billableYards} yds /
                                    </span>{' '}
                                    Custom
                                  </p>
                                  <p className="text-xs text-brand-orange mt-0.5" data-id="element-3003" data-mp="3021dvj9lpisf">
                                    text us for pricing
                                  </p>
                                </> : <>
                                  {/* Secondary metadata ("{N} yds /"
                                   and "+ delivery") uses the same
                                   neutral muted-grey as the left
                                   column's "/ yard" so the surrounding
                                   metadata reads uniformly white →
                                   grey regardless of discount state.
                                   Only the BIG numbers stay white. */}
                                  <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-3004" data-mp="1xlkw1h1dko0d">
                                    <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-3005" data-mp="27h4nnr8wp0jb">
                                      {billableYards} yds /
                                    </span>{' '}
                                    ${totalCost.toLocaleString()}
                                  </p>
                                  {/* "+ delivery" mirrors the green
                                   "Price" label on the left so the
                                   two columns book-end the row in
                                   matching accent colors. */}
                                  <p className="text-xs text-brand-muted dark:text-[#22c95c] mt-0.5" data-id="element-3006" data-mp="306lc6dtenxa9">
                                    + delivery
                                  </p>
                                </>}
                            </div>}
                        </div>

                        {/* Section-wide explainer pulldown for the
                         Small-load rate label. Spans the entire price
                         card so it has room to breathe (rather than
                         squeezing into the left column under the
                         numbers). */}
                        <AnimatePresence data-id="element-3007" data-mp="jj3kbgnlvmiu">
                          {showSmallLoadInfo && !isCustomQuoteZone && showsEffective && hasSurchargedLoad && !bulkPct && <motion.div id="small-load-rate-explainer" initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-3008" data-mp="oh5300h4djts">
                                <p className="mt-3 p-3 rounded-lg bg-[#CB6E0A]/10 dark:bg-[#F6A93B]/10 border border-[#CB6E0A]/30 dark:border-[#F6A93B]/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed" data-id="element-3009" data-mp="3kfnukb4q9ff2">
                                  A small load runs the truck below capacity.
                                  Fixed loading costs (loader operator, scale,
                                  ticketing) get split across fewer yards, so
                                  the per-yard rate goes up. Order{' '}
                                  {smallLoadThreshold}+ yd in a single delivery
                                  to drop to the base ${product.pricePerYard}/yd
                                  rate.
                                </p>
                              </motion.div>}
                        </AnimatePresence>

                      </div>;
              })()}

                  {/* Estimator handoff banner — appears when the user
                   arrived from the Instant Estimator with a custom yardage
                   that needs to split across multiple deliveries. Without
                   this chip, the dropdown still shows its default ("12 Yards")
                   and the user thinks the page ignored their calculated
                   amount. Clicking "Change quantity" reverts to the dropdown. */}
                  {estimatorQty && estimatorQty > 0 && (() => {
                const loads = (() => {
                  const fullLoad = isLight ? 15 : 12;
                  const MIN = 5;
                  const n = estimatorQty;
                  if (n <= fullLoad) return [Math.max(MIN, n)];
                  const count = Math.ceil(n / fullLoad);
                  const last = n - (count - 1) * fullLoad;
                  const billableLast = Math.max(MIN, last);
                  const arr: number[] = [];
                  for (let i = 0; i < count - 1; i++) arr.push(fullLoad);
                  arr.push(billableLast);
                  return arr;
                })();
                const breakdown = loads.join(' + ');
                const totalBillable = loads.reduce((a, b) => a + b, 0);
                return <motion.div initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="rounded-xl border border-[#04A05C]/30 bg-[#04A05C]/10 p-3 sm:p-4" data-id="element-3010" data-mp="dt0x7p2xfp81">
                          <div className="flex items-start gap-3" data-id="element-3011" data-mp="3d8fes1rdfn34">
                            <div className="w-8 h-8 rounded-full bg-[#04A05C]/20 flex items-center justify-center shrink-0" data-id="element-3012" data-mp="3r2yj4iqvl0o2">
                              <Check className="w-4 h-4 text-[#04A05C]" data-id="element-3013" data-mp="3m9vr4f1qxz9z" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3014" data-mp="2yjj7w811wg43">
                              <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-3015" data-mp="2tlhtw1ihf6zh">
                                From your estimator: {estimatorQty}{' '}
                                {estimatorQty === 1 ? 'yard' : 'yards'}
                              </p>
                              <p className="text-xs text-brand-charcoal/70 dark:text-stone-300 mt-0.5" data-id="element-3016" data-mp="2i0unkg9nly29">
                                Splits into {loads.length}{' '}
                                {loads.length === 1 ? 'delivery' : 'deliveries'}{' '}
                                ({breakdown} yd)
                                {totalBillable !== estimatorQty && <span className="text-amber-700 dark:text-amber-400" data-id="element-3017" data-mp="2vzlzbw11n7n8">
                                    {' '}
                                    · billed as {totalBillable} yd (3 yd order
                                    min)
                                  </span>}
                              </p>
                            </div>
                            <button type="button" onClick={() => setEstimatorQty(null)} className="shrink-0 text-xs font-semibold text-brand-charcoal/70 dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange underline underline-offset-2" data-id="element-3018" data-mp="3fomo9k7x7hel">
                              Change
                            </button>
                          </div>
                        </motion.div>;
              })()}

                  <div className="space-y-2" data-id="element-3019" data-mp="p5bhix0xj128">
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-3020" data-mp="p6mr5dh23319">
                      <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-3021" data-mp="1mrimw9524xfn">
                        {estimatorQty ? 'Or change quantity' : 'Select Quantity'}
                      </label>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                    const tier3Yards = fullLoad * 4;
                    return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-3022" data-mp="20sblb4yhcco1">
                            5% at {tier2Yards} yd · 10% at {tier3Yards} yd
                          </span>;
                  })()}
                    </div>
                    {/* Floating stepper — no container background, just
                     big bold + and − pads flanking the live readout.
                     active:* gives instant green flash on press. Below
                     a full load increments by 1 yard; at/above a full
                     load snaps to the next full-load multiple (2, 3,
                     4 loads). + at 4 loads flips the row below into an
                     amber "Need 5+ loads?" link (does NOT auto-open the
                     custom-quote modal — the customer explicitly taps
                     the link). */}
                    <div className="flex flex-col gap-3" data-id="element-3023" data-mp="3q7b8dffegw5g">
                      <div className="flex min-h-[68px] items-stretch gap-3 sm:min-h-[79px]" data-id="element-3024" data-mp="8051kdex52kh">
                        <div className="flex min-w-0 flex-1 items-center rounded-lg border border-stone-300 bg-transparent px-5 text-2xl font-extrabold leading-none tabular-nums text-brand-charcoal dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white sm:px-6 sm:text-3xl" data-id="element-3025" data-mp="32bt1dq57wh1">
                          {(() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        const loadCount = current > fullLoad ? Math.round(current / fullLoad) : 1;
                        const isFullLoad = current === fullLoad;
                        const isMin = current === 3;
                        const commit = () => {
                          const parsed = parseInt(qtyDraft, 10);
                          if (isNaN(parsed) || parsed <= 0) {
                            // Empty / invalid → revert input text to current.
                            setQtyDraft(String(current));
                            return;
                          }
                          // Cap at 8 loads (96 yd heavy / 120 yd light).
                          const max = fullLoad * 8;
                          const clamped = Math.min(max, Math.max(1, parsed));
                          setQuantity(String(clamped));
                          setQtyDraft(String(clamped));
                          // Flip the amber "text us" CTA once past 4 loads.
                          if (clamped > fullLoad * 4) {
                            setShowQuoteWarning(true);
                          } else {
                            setShowQuoteWarning(false);
                          }
                        };
                        return <>
                                <input ref={qtyInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={qtyDraft} maxLength={2} onFocus={e => e.currentTarget.select()} onChange={e => {
                            // Numeric only, max 2 digits.
                            const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                            setQtyDraft(v);
                          }} onBlur={commit} onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.currentTarget as HTMLInputElement).blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setQtyDraft(String(current));
                              (e.currentTarget as HTMLInputElement).blur();
                            }
                          }} aria-label="Quantity in yards" style={{
                            width: `${Math.max(1, qtyDraft.length)}ch`
                          }} className={`bg-transparent border-0 outline-none text-center text-2xl font-bold tabular-nums p-0 m-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3026" data-mp="21jn0ns96r0pv" />
                                <span className={`hidden sm:inline transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3027" data-mp="13ua1zgsom04l">
                                  {' '}
                                  yards
                                </span>
                                <span className={`sm:hidden transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3028" data-mp="18vh8lcj38t9o">
                                  {' '}
                                  yd
                                </span>
                                {(loadCount > 1 || loadCount === 1 && Number(quantity) === (isLight ? 15 : 12)) && <span className="ml-2 text-xs sm:text-sm font-medium text-brand-muted dark:text-stone-400 align-baseline whitespace-nowrap" data-id="element-3029" data-mp="q3ya9j00e3dz">
                                    ({loadCount}{' '}
                                    {loadCount === 1 ? 'load' : 'loads'})
                                  </span>}
                              </>;
                      })()}
                        </div>
                        <div className="flex shrink-0 gap-3" data-id="element-3030" data-mp="2duw9a3t84844">
                          <button type="button" onClick={() => {
                        const fullLoad = isLight ? 15 : 12;
                        const MIN = 3;
                        const current = Number(quantity) || 12;
                        if (current <= fullLoad) {
                          if (current > MIN) setQuantity(String(current - 1));
                        } else {
                          const currentLoads = Math.round(current / fullLoad);
                          const newLoads = currentLoads - 1;
                          setQuantity(String(newLoads * fullLoad));
                          // Clear the amber CTA once back at 4 loads or less.
                          if (newLoads <= 4) setShowQuoteWarning(false);
                        }
                      }} aria-label="Decrease quantity" style={{
                        WebkitTapHighlightColor: 'transparent'
                      }} className="group flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-transparent text-brand-charcoal transition-colors duration-150 hover:bg-[#059F40]/10 active:bg-[#059F40]/20 active:text-[#059F40] dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white dark:hover:bg-[#333333] dark:active:bg-[#22c95c]/15 dark:active:text-[#22c95c] sm:h-[72px] sm:w-[72px]" data-id="element-3031" data-mp="202g5h0si0625">
                            <Minus className="h-8 w-8 transition-transform duration-150 ease-out group-active:scale-125 sm:h-9 sm:w-9" strokeWidth={2.5} data-id="element-3032" data-mp="2qc6zebbonu1h" />
                          </button>
                          <button type="button" onClick={() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        if (current < fullLoad) {
                          setQuantity(String(current + 1));
                        } else {
                          const currentLoads = Math.round(current / fullLoad);
                          // Cap at 8 loads. Past 4 loads the customer
                          // is in "large order" territory and the
                          // primary CTA flips to the amber "text us"
                          // button — but the stepper still works so
                          // they can show us the exact yardage they
                          // need before tapping through to SMS.
                          if (currentLoads >= 8) return;
                          const newLoads = currentLoads + 1;
                          setQuantity(String(newLoads * fullLoad));
                          if (newLoads >= 5) setShowQuoteWarning(true);
                        }
                      }} aria-label="Increase quantity" style={{
                        WebkitTapHighlightColor: 'transparent'
                      }} className="group flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-transparent text-brand-charcoal transition-colors duration-150 hover:bg-[#059F40]/10 active:bg-[#059F40]/20 active:text-[#059F40] dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white dark:hover:bg-[#333333] dark:active:bg-[#22c95c]/15 dark:active:text-[#22c95c] sm:h-[72px] sm:w-[72px]" data-id="element-3033" data-mp="2hcab4at7gx6y">
                            <Plus className="h-8 w-8 transition-transform duration-150 ease-out group-active:scale-125 sm:h-9 sm:w-9" strokeWidth={2.5} data-id="element-3034" data-mp="2jl1bl1waidoe" />
                          </button>
                        </div>
                      </div>

                      {/* Large-order handoff — when + has pushed past
                       4 loads, the primary CTA below morphs into an
                       amber "Text us for a quote" button instead of a
                       separate amber pulldown. */}

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="space-y-3" data-id="element-3035" data-mp="18xdf1qqrzrwj">
                          <div className="w-full flex items-center justify-center bg-[#04A05C]/10 dark:bg-[#006241]/40 text-[#04A05C] dark:text-[#22c95c] rounded-none font-bold border border-[#04A05C] dark:border-[#22c95c] py-4 text-lg" data-id="element-3036" data-mp="12tgya6hfjohi">
                            <Check className="w-5 h-5 mr-2" data-id="element-3037" data-mp="u03vmctkd8i8" />
                            Added to Truck!
                          </div>
                          <div className="flex gap-2" data-id="element-3038" data-mp="k4j10zqtraii">
                            <button onClick={() => {
                        setAddedFlash(false);
                        navigate('/#materials');
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-brand-charcoal dark:text-white border border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-3039" data-mp="k269pvz6fnss">
                              Continue Shopping
                            </button>
                            <button onClick={() => {
                        setAddedFlash(false);
                        openCart();
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-orange hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-1.5" data-id="element-3040" data-mp="390gxfd0x1g81">
                              <ShoppingCart className="w-4 h-4" data-id="element-3041" data-mp="3rs9r79nt75gt" />
                              View Cart
                            </button>
                          </div>
                        </motion.div> : showQuoteWarning ? <button onClick={() => {
                    const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                    if (!isMobile) {
                      setIsQuoteModalOpen(true);
                      return;
                    }
                    const fullLoad = isLight ? 15 : 12;
                    const yds = Number(quantity) || fullLoad * 5;
                    const loads = Math.max(5, Math.round(yds / fullLoad));
                    const body = `Hi 206Gravel — I need a custom quote for ${loads} loads of ${product.name} (${yds} yd). Thanks!`;
                    window.location.href = `sms:+12066193742?&body=${encodeURIComponent(body)}`;
                  }} className="w-full py-[18px] rounded-none font-bold text-lg transition-all duration-300 flex items-center justify-center bg-[#DD4132]/15 dark:bg-[#DD4132]/15 hover:bg-[#DD4132]/25 dark:hover:bg-[#DD4132]/25 text-[#DD4132] dark:text-[#DD4132] border-2 border-[#DD4132]/40 hover:border-[#DD4132]/60" aria-live="polite" data-id="element-3042" data-mp="3irl8w2rduiy4">
                          <MessageSquare className="w-5 h-5 mr-2" data-id="element-3043" data-mp="1uvn9zh7zf0lw" />
                          Large order — text us →
                        </button> : (() => {
                    const current = Number(quantity) || 12;
                    const fullLoad = isLight ? 15 : 12;
                    const loadPct = Math.min(100, Math.max(0, current / fullLoad * 100));
                    return <button onClick={handleAddToCart} className="relative w-full overflow-hidden bg-[#006239] text-white py-[21px] rounded-none font-bold text-lg flex items-center justify-center shadow-[0_0_20px_rgba(5,159,64,0.3)]" data-id="element-3044" data-mp="1n22c8fg8sq81">
                              <motion.span aria-hidden="true" className="absolute inset-y-0 left-0 bg-[#04A05C]" initial={false} animate={{
                        width: `${loadPct}%`
                      }} transition={{
                        type: 'spring',
                        stiffness: 260,
                        damping: 26
                      }} data-id="element-3045" data-mp="20tyyi2v8c5wi" />
                              <span className="relative z-10 flex items-center justify-center" data-id="element-3046" data-mp="1d332etjqqojg">
                                <Truck className="w-5 h-5 mr-2" data-id="element-3047" data-mp="1i4shcka33mct" />
                                Add to Truck
                              </span>
                            </button>;
                  })()}
                    </div>

                    <div className="flex justify-center" data-id="element-3048" data-mp="hvauvdlwftg1">
                      <SameDayBadge variant="full" data-id="element-3049" data-mp="hurew2ft7f4n" />
                    </div>

                    {/* Material sanity check — helps the customer confirm
                     the product they're viewing matches their project
                     BEFORE adding to cart. Lives here (not in the cart
                     drawer) so the decision happens with intent fresh.
                     The helper is now category-aware: each category in
                     data/products.ts ships its own pair of questions
                     and its own recommendation table inside
                     components/CartHelper.tsx, so the suggestions stay
                     relevant whether the customer is browsing base rock,
                     clear & drain rock, soil/mulch, quarry rip-rap, or
                     decorative landscape stone. */}
                    <CartHelper contextProduct={{
                  slug: product.slug,
                  name: product.name
                }} data-id="element-3050" data-mp="1eljs8sbcyt2q" />

                    {/* Multi-load delivery-fee notice — visible whenever
                     the stepper is open. The "please select how many"
                     prompt is no longer needed because the stepper
                     auto-seeds 2 loads on entering multi mode. */}
                    <AnimatePresence data-id="element-3051" data-mp="1hmd8t3qw5vjl">
                      {quantity === 'multi' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-3052" data-mp="13vrn77a1z7i0">
                          <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-3053" data-mp="yxgdqxno6z0b">
                            <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-3054" data-mp="kz9na2l1w1ao" />
                            <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-3055" data-mp="p0lg0gz812n3">
                              Multi-load orders require separate deliveries. You
                              will be charged a delivery fee per load.
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                </div>}
            </div>
          </div>
        </div>

        <section className="mt-10" aria-labelledby="product-support-heading" data-id="element-3056" data-mp="p7rm9c9tvyno">
          <div className="mb-5" data-id="element-3057" data-mp="karhd4opt1y5">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#05894e] dark:text-[#4ed491]" data-id="element-3058" data-mp="k9gb8y7sri3v">Plan with confidence</p>
            <h2 id="product-support-heading" className="mt-1 font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-3059" data-mp="tzrfklve7tba">Resources for your project</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3060" data-mp="18026tq0m79wz">Get the quantity right, prepare your delivery space, and use practical project guidance before your material arrives.</p>
          </div>

          <div className={`grid gap-4 ${isCalcOpen ? 'md:grid-cols-1 md:items-start' : 'md:grid-cols-3 md:items-stretch'}`} data-id="element-3061" data-mp="4omtgdlgjzle">
            <div className="space-y-4 md:flex md:h-full md:flex-col" data-id="element-3062" data-mp="9nhivydxkmeh">
              <button type="button" onClick={() => setIsCalcOpen(value => !value)} aria-expanded={isCalcOpen} className="w-full rounded-xl border border-[#05894e]/35 bg-[#05894e]/5 p-5 text-left transition-colors hover:bg-[#05894e]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:bg-[#05894e]/10 md:flex-1" data-id="element-3063" data-mp="eeg3ha5yc6nj">
                <CalcIcon className="h-6 w-6 text-[#04A05C] dark:text-[#04A05C]" aria-hidden="true" data-id="element-3064" data-mp="sfafs3w4errh" />
                <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3065" data-mp="xc93a3la40gp">Calculate how much you need</h3>
                <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3066" data-mp="2u75bha1zelo2">Estimate yards from your project dimensions and apply the result directly to this order.</p>
                <span className="mt-4 inline-flex text-sm font-bold text-[#05894e] dark:text-[#04A05C] md:mt-auto md:pt-4" data-id="element-3067" data-mp="2rlxtcp121slk">{isCalcOpen ? 'Hide calculator' : 'Open calculator'}</span>
              </button>
              <AnimatePresence data-id="element-3068" data-mp="2hw4gzlt399g9">
                {isCalcOpen && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.22
              }} className="overflow-hidden" data-id="element-3069" data-mp="2cw9pph5eihuo">
                    <div className="border-t border-[#05894e]/20 pt-5 dark:border-[#05894e]/25" data-id="element-3070" data-mp="30k17v1dmhp3v">
                      <SimpleAreaCalculator materialSlug={product.slug} onApply={handleUseQuantity} data-id="element-3071" data-mp="2wjza33b0dftv" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/delivery-prep" className="flex flex-col rounded-xl border border-stone-200 bg-[#fffcf9] p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:border-white/10 dark:bg-[#222222] dark:hover:bg-[#303030] md:h-full" data-id="element-3072" data-mp="1l76ns80zljl7">
              <Truck className="h-6 w-6 text-[#04A05C] dark:text-[#04A05C]" aria-hidden="true" data-id="element-3073" data-mp="3g88r5zximsz2" />
              <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3074" data-mp="7sfa9jz9vfhh">Delivery prep guide</h3>
              <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3075" data-mp="bu0rtimr4hy0">Check access, placement, and site conditions so delivery day goes smoothly.</p>
              <span className="mt-4 inline-flex text-sm font-bold text-[#04A05C] dark:text-[#04A05C] md:mt-auto md:pt-4" data-id="element-3076" data-mp="3u5xct9zeq456">Prepare your site <ArrowRight className="ml-1 h-4 w-4" data-id="element-3077" data-mp="qrb4gvb4rtvb" /></span>
            </Link>
            <Link to="/blog" className="flex flex-col rounded-xl border border-stone-200 bg-[#fffcf9] p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:border-white/10 dark:bg-[#222222] dark:hover:bg-[#303030] md:h-full" data-id="element-3078" data-mp="qp83xhccqicc">
              <FileText className="h-6 w-6 text-[#04A05C] dark:text-[#04A05C]" aria-hidden="true" data-id="element-3079" data-mp="10tf014vdqoiw" />
              <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3080" data-mp="1eoh7vxjsenyc">DIY project guides</h3>
              <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3081" data-mp="lxz4evf5ikke">Learn the right material depth, base layers, and installation approach for common projects.</p>
              <span className="mt-4 inline-flex text-sm font-bold text-[#04A05C] dark:text-[#04A05C] md:mt-auto md:pt-4" data-id="element-3082" data-mp="hyqclw3o8wrh">Explore guides <ArrowRight className="ml-1 h-4 w-4" data-id="element-3083" data-mp="qxbox9ijvfjt" /></span>
            </Link>
          </div>
        </section>

        <ProductFAQs product={product} category={category} data-id="element-3084" data-mp="27asd5lic9d1h" />

        {(relatedProjects.length > 0 || supportedCities.length > 0) && <section className="mt-10 border-t border-stone-200 pt-8 dark:border-white/10" aria-labelledby="product-explore-heading" data-id="element-3085" data-mp="22bfjohwyp7px">
            <div className="mb-5" data-id="element-3086" data-mp="3rat4deljcqht">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#05894e] dark:text-[#4ed491]" data-id="element-3087" data-mp="3mdb0w3qaf3h7">Keep exploring</p>
              <h2 id="product-explore-heading" className="mt-1 font-heading text-2xl font-bold text-brand-charcoal dark:text-white" data-id="element-3088" data-mp="3id0nw742vcsu">Plan your delivery with {product.name}</h2>
            </div>
            <div className="grid gap-4 lg:grid-cols-2" data-id="element-3089" data-mp="dwpose2czu9v">
              {relatedProjects.length > 0 && <div className="rounded-xl border border-stone-200 bg-[#fffcf9] p-5 dark:border-white/10 dark:bg-[#222222]" data-id="element-3090" data-mp="9xgvfab8dyeu">
                  <h3 className="font-bold text-brand-charcoal dark:text-white" data-id="element-3091" data-mp="544kg2zh5q4k">Recommended project guides</h3>
                  <ul className="mt-3 space-y-2" data-id="element-3092" data-mp="6cjkjguqto8">
                    {relatedProjects.map(project => <li key={project.slug} data-id="element-3093" data-mp="xnjl5jroasaa">
                        <Link to={`/projects/${project.slug}`} className="inline-flex items-center text-sm font-semibold text-[#05894e] hover:underline dark:text-[#4ed491]" data-id="element-3094" data-mp="svjqa5qgmpkk">
                          {project.name}
                          <ArrowRight className="ml-1 h-3.5 w-3.5" data-id="element-3095" data-mp="154zp211aycsx" />
                        </Link>
                      </li>)}
                  </ul>
                </div>}
              {supportedCities.length > 0 && <div className="rounded-xl border border-stone-200 bg-[#fffcf9] p-5 dark:border-white/10 dark:bg-[#222222]" data-id="element-3096" data-mp="1e6pej1bmj1o2">
                  <h3 className="font-bold text-brand-charcoal dark:text-white" data-id="element-3097" data-mp="1j4hhcqdt4871">Available across Puget Sound</h3>
                  <ul className="mt-3 space-y-2" data-id="element-3098" data-mp="1o2smint5puix">
                    {supportedCities.map(city => <li key={city.slug} data-id="element-3099" data-mp="1sw54if3bz32p">
                        <Link to={`/gravel-delivery/${city.slug}/${product.slug}`} className="inline-flex items-center text-sm font-semibold text-[#05894e] hover:underline dark:text-[#4ed491]" data-id="element-3100" data-mp="1sxg5y89azyqn">
                          {product.name} delivery in {city.name}
                          <ArrowRight className="ml-1 h-3.5 w-3.5" data-id="element-3101" data-mp="26vmwt4fu4ygt" />
                        </Link>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </section>}
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-3102" data-mp="2sv1m5liio4n" />

      {/* Old fixed-position Calculator Drawer removed — replaced by the
                                                                                                                                                                                                                                                                          inline "Calculate how much I need" pulldown under the Add to Truck
                                                                                                                                                                                                                                                                          button, matching the Services hub product detail layout. */}
    </motion.main>;
}